import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { useSynchronizedPending } from "../../components/nucleus_explain/use_synchronized_pending";
import { defaultConfig } from "./default_config";
import { PivotControl } from "../../constants/pivot_control_options";
import { defaultChartViewerConfig } from "./default_chart_viewer_config";
import { ChatContext } from "../../contexts/chat/context";
import { LayoutContext } from "../../contexts/layout/context";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useCustomer } from "../../contexts/app/provider";
import {
  createDemandCellContext,
  demandCellPrompt,
  isSupportedDemandCellRecipe,
  type CommonCellContextV3,
} from "../../entities/demand_cell_context";
import { commonRunEditState } from "../../entities/common_run_edit_state";
import {
  renderedViewBindingIdentity,
  type RenderedViewBinding,
  type CellExplanationCapture,
  type PivotCellExplanationConfig,
} from "../../entities/pivot_view_config";
import type { DatePivotParams } from "../../entities/date_pivot_params";
import {
  createRenderedDatePivotCellContext,
  isSupportedRenderedDatePivotCellContext,
  renderedDatePivotCellPrompt,
  type CommonRenderedCellContextV2,
} from "../../entities/rendered_cell_context";
import {
  createCommonViewContext,
  type CommonViewContextV1,
} from "../../entities/common_view_context";
import { isCommonCurrentViewReceiptId } from "../../contexts/chat/nucleus_sse_client";
import { isCommonNucleusExplainabilityCustomer } from "../../entities/common_nucleus_customer_capabilities";
import {
  awaitNucleusExplainStart,
  NUCLEUS_EXPLAIN_BUSY_REASON,
  NUCLEUS_EXPLAIN_STALE_REASON,
  rejectNucleusExplainActivation,
} from "../../entities/nucleus_explain_action";

type PendingDemandCell = Readonly<{
  context: CommonCellContextV3 | CommonRenderedCellContextV2;
  viewContext?: CommonViewContextV1;
  prompt: string;
  runIdentity: string;
  sourceIdentity: string;
  receiptId?: string;
  connectionReady: boolean;
}>;

const getRunIdentity = (
  run: React.ContextType<typeof VersionedDataContext>["mainRun"] | undefined,
): string | undefined =>
  run
    ? [
        run.runSettings.organizationId,
        run.runSettings.inputId,
        run.runSettings.workflowId,
        run.runSettings.settingsNameAndVersion,
        run.runSettings.runAttemptId,
      ].join("\u0000")
    : undefined;

export const DemandNoPerspectiveContainer = () => {
  const { selectedCustomerId } = useCustomer();
  const {
    mainRun,
    lastSuccessfulRun,
    comparedRun = null,
  } = useContext(VersionedDataContext);
  const { isComparing, setIsSidebarOpen } = useContext(LayoutContext);
  const {
    connectChat,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    registerCurrentViewData,
    sendMessage,
    transportKind,
  } = useContext(ChatContext);
  const activeRun = mainRun || lastSuccessfulRun;
  const activeRunIdentity = getRunIdentity(activeRun);
  const activeRunIdentityRef = useRef(activeRunIdentity);
  activeRunIdentityRef.current = activeRunIdentity;
  const {
    pending: pendingDemandCell,
    pendingRef: pendingDemandCellRef,
    setPending: setPendingDemandCell,
    clearPending: clearPendingDemandCell,
    replacePending: replacePendingDemandCell,
  } = useSynchronizedPending<PendingDemandCell>();
  const currentRenderedSourceIdentityRef = useRef<string | undefined>(
    undefined,
  );
  const hasRenderedSourceSignalRef = useRef(false);
  const releaseCurrentViewDataRef = useRef<(() => void) | null>(null);

  const supportsDemandCellExplain = Boolean(
    transportKind === "platform" &&
      activeRun &&
      activeRunIdentity &&
      activeRun.status === "RUN_COMPLETED" &&
      activeRun.runSettings.workflowId === "workflow_atomic_pivots" &&
      commonRunEditState(activeRun.runSettings.settingsNameAndVersion) &&
      !isComparing &&
      comparedRun === null &&
      isCommonNucleusExplainabilityCustomer(
        activeRun.runSettings.organizationId,
      ) &&
      (!selectedCustomerId ||
        selectedCustomerId === activeRun.runSettings.organizationId),
  );
  const supportsDemandCellExplainRef = useRef(supportsDemandCellExplain);
  supportsDemandCellExplainRef.current = supportsDemandCellExplain;

  const handleRenderedSourceChange = useCallback(
    (source: RenderedViewBinding | undefined, viewParams?: DatePivotParams) => {
      hasRenderedSourceSignalRef.current = true;
      const sourceIdentity = source
        ? renderedViewBindingIdentity(source)
        : undefined;
      currentRenderedSourceIdentityRef.current = sourceIdentity;
      const pending = pendingDemandCellRef.current;
      if (pending && pending.sourceIdentity !== sourceIdentity) {
        clearPendingDemandCell(pending);
      }
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
      const viewContext = viewParams
        ? createCommonViewContext({
            surfaceId: "demand",
            measure: viewParams.measure,
            dateHierarchy: viewParams.dateHierarchy,
            minDate: viewParams.minDate,
            maxDate: viewParams.maxDate,
            segments: viewParams.segments,
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          })
        : undefined;
      if (
        supportsDemandCellExplain &&
        registerCurrentViewData &&
        isCommonCurrentViewReceiptId(source?.receiptId) &&
        viewContext &&
        getRunIdentity(source.run) === activeRunIdentity
      ) {
        releaseCurrentViewDataRef.current = registerCurrentViewData(
          {
            version: "CommonCurrentViewData.v1",
            surfaceId: "demand",
            receiptId: source.receiptId,
          },
          viewContext,
        );
      }
    },
    [
      activeRunIdentity,
      clearPendingDemandCell,
      pendingDemandCellRef,
      registerCurrentViewData,
      supportsDemandCellExplain,
    ],
  );

  useEffect(
    () => () => {
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
    },
    [],
  );

  const captureDemandCell = useCallback<
    PivotCellExplanationConfig<DatePivotParams>["capture"]
  >(
    ({ columnKey, row, value, viewParams, source }) => {
      if (!supportsDemandCellExplain || !activeRun) return undefined;
      const viewContext = createCommonViewContext({
        surfaceId: "demand",
        measure: viewParams.measure,
        dateHierarchy: viewParams.dateHierarchy,
        minDate: viewParams.minDate,
        maxDate: viewParams.maxDate,
        segments: viewParams.segments,
        filters: viewParams.filters,
        postFilters: viewParams.postFilters,
      });
      const renderedRunIdentity = getRunIdentity(source?.run);
      if (
        source?.receiptId &&
        activeRunIdentity &&
        renderedRunIdentity === activeRunIdentity
      ) {
        const renderedContext = createRenderedDatePivotCellContext({
          surfaceId: "demand",
          receiptId: source.receiptId,
          columnKey,
          segments: viewParams.segments,
          row,
          measure: viewParams.measure,
          dateField: viewParams.dateHierarchy,
          rawValue: value,
        });
        if (
          renderedContext &&
          isSupportedRenderedDatePivotCellContext(renderedContext)
        ) {
          return Object.freeze({
            prompt: renderedDatePivotCellPrompt(renderedContext, viewContext),
            metricContext: renderedContext,
            viewContext,
          });
        }
      }

      const context = createDemandCellContext({
        measure: viewParams.measure,
        periodField: viewParams.dateHierarchy,
        monthStartEpochMs: Number(columnKey),
        segments: viewParams.segments,
        row,
        filters: viewParams.filters,
        postFilters: viewParams.postFilters ?? [],
        rawValue: value,
        settingsNameAndVersion: activeRun.runSettings.settingsNameAndVersion,
        comparison: isComparing,
      });
      if (context && isSupportedDemandCellRecipe(context)) {
        return Object.freeze({
          prompt: demandCellPrompt(context),
          metricContext: context,
          viewContext,
        });
      }

      return undefined;
    },
    [activeRun, activeRunIdentity, isComparing, supportsDemandCellExplain],
  );

  const explainDemandCell = useCallback(
    (capture: CellExplanationCapture) => {
      const context = capture.metricContext;
      const renderedRun = capture.source.run;
      const renderedRunIdentity = getRunIdentity(renderedRun);
      const sourceIdentity = renderedViewBindingIdentity(capture.source);
      const isDedicatedContext =
        context.version === "CommonCellContext.v3" &&
        isSupportedDemandCellRecipe(context);
      const isRenderedContext =
        context.version === "CommonRenderedCellContext.v2" &&
        context.surfaceId === "demand" &&
        isSupportedRenderedDatePivotCellContext(context) &&
        context.receiptId === capture.source.receiptId;
      if (
        !supportsDemandCellExplain ||
        (!isDedicatedContext && !isRenderedContext) ||
        !activeRun ||
        !activeRunIdentity ||
        !renderedRunIdentity ||
        renderedRunIdentity !== activeRunIdentity ||
        renderedRun.status !== "RUN_COMPLETED" ||
        renderedRun.runSettings.workflowId !== "workflow_atomic_pivots" ||
        !commonRunEditState(renderedRun.runSettings.settingsNameAndVersion) ||
        !isCommonNucleusExplainabilityCustomer(
          renderedRun.runSettings.organizationId,
        ) ||
        (Boolean(selectedCustomerId) &&
          selectedCustomerId !== renderedRun.runSettings.organizationId) ||
        (hasRenderedSourceSignalRef.current &&
          currentRenderedSourceIdentityRef.current !== sourceIdentity)
      ) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }
      if (isAwaitingResponse || pendingDemandCellRef.current) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_BUSY_REASON);
      }

      const pendingCell = Object.freeze({
        context,
        viewContext: capture.viewContext,
        prompt: capture.prompt,
        runIdentity: renderedRunIdentity,
        sourceIdentity,
        ...(isRenderedContext ? { receiptId: context.receiptId } : {}),
        connectionReady: false,
      } as const);
      if (!hasRenderedSourceSignalRef.current) {
        currentRenderedSourceIdentityRef.current = sourceIdentity;
      }
      // The provider keeps duplicate readiness requests for this scope
      // idempotent.
      setPendingDemandCell(pendingCell);
      // A cell action must acknowledge the click immediately. Readiness and
      // evidence work continue inside the already-visible Nucleus panel.
      setIsSidebarOpen(true);
      return awaitNucleusExplainStart(
        () => connectChat(renderedRun, "demand"),
        () => clearPendingDemandCell(pendingCell),
        () => {
          if (
            !supportsDemandCellExplainRef.current ||
            activeRunIdentityRef.current !== pendingCell.runIdentity ||
            currentRenderedSourceIdentityRef.current !==
              pendingCell.sourceIdentity
          ) {
            return false;
          }
          return replacePendingDemandCell(
            pendingCell,
            Object.freeze({ ...pendingCell, connectionReady: true }),
          );
        },
      );
    },
    [
      activeRun,
      activeRunIdentity,
      clearPendingDemandCell,
      connectChat,
      isAwaitingResponse,
      pendingDemandCellRef,
      replacePendingDemandCell,
      selectedCustomerId,
      setPendingDemandCell,
      setIsSidebarOpen,
      supportsDemandCellExplain,
    ],
  );

  useEffect(() => {
    if (!pendingDemandCell) return;
    if (
      !supportsDemandCellExplain ||
      pendingDemandCell.runIdentity !== activeRunIdentity ||
      currentRenderedSourceIdentityRef.current !==
        pendingDemandCell.sourceIdentity ||
      (pendingDemandCell.context.version === "CommonCellContext.v3"
        ? !isSupportedDemandCellRecipe(pendingDemandCell.context)
        : pendingDemandCell.context.surfaceId !== "demand" ||
          !isSupportedRenderedDatePivotCellContext(pendingDemandCell.context) ||
          pendingDemandCell.context.receiptId !== pendingDemandCell.receiptId)
    ) {
      clearPendingDemandCell(pendingDemandCell);
      return;
    }
    if (!pendingDemandCell.connectionReady) return;
    if (!isConnected || isConnecting || isAwaitingResponse) return;

    sendMessage(
      pendingDemandCell.prompt,
      pendingDemandCell.context,
      pendingDemandCell.viewContext,
    );
    clearPendingDemandCell(pendingDemandCell);
  }, [
    activeRunIdentity,
    clearPendingDemandCell,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    pendingDemandCell,
    sendMessage,
    supportsDemandCellExplain,
  ]);

  const cellExplanation = useMemo(
    () =>
      supportsDemandCellExplain
        ? {
            capture: captureDemandCell,
            onExplain: explainDemandCell,
          }
        : undefined,
    [captureDemandCell, explainDemandCell, supportsDemandCellExplain],
  );

  return (
    <TransformedCustomViewContainer
      viewControllerConfig={{
        datasetName: "sales_order_master",
        transformId: "sales_order_master_date_pivot",
        showActualsForecastStrip: true,
        // Sales orders behind an ACTUALS cell (sales_order_details). Sits
        // alongside the Nucleus Explain action rather than replacing it.
        enableSalesOrderPopover: true,
        folder: "outputs",
        title: "Demand",
        viewContextSurfaceId: "demand",
        dateHierarchyColumnFilter: {
          order_placed_month: "month",
          order_placed_week: "week",
          order_placed_year: "year",
          order_placed_quarter: "quarter",
          order_placed_date: "day",
        },
        excludedSegmentationsFilter: [],
        measureColumnFilter: {
          quantity: "quantity",
          revenue: "revenue",
          cost: "cost",
          weight_in_pounds: "pounds",
          // Schema-gated: only shown for customers whose sales_order_master
          // carries this physical column (currently Cora).
          eaches: "eaches",
        },
        excludedFilterColumnFilter: [],
        showControls: [
          PivotControl.DateHierarchy,
          PivotControl.DatePeriodPicker,
          PivotControl.FilterBy,
          PivotControl.Measure,
          PivotControl.SegmentSelectMulti,
          PivotControl.StoreAsDefault,
          PivotControl.Download,
          PivotControl.ShowSettings,
          PivotControl.EnableChartView,
        ],
        shadeHistory: true,
        showHeaderRow: true,
        cellExplanation,
        cellExplanationEnabled: !isAwaitingResponse && !pendingDemandCell,
        onRenderedSourceChange: handleRenderedSourceChange,
      }}
      defaultConfig={defaultConfig}
      chartConfig={defaultChartViewerConfig}
    />
  );
};
